/**
 * Загрузка одного блока районной вкладки «Цифрового паспорта» (US-12
 * FR-1.1): у каждого блока своя операция, и они запрашиваются независимо
 * — месячный бюджет не ждёт годового среза населённых пунктов, а сбой
 * одного источника не обнуляет наполненные блоки.
 *
 * Выключенный супер-админом и неприменимый к территории блок не
 * запрашивается вовсе (`enabled = false`): лишний 200 со `status:
 * DISABLED` ничего экрану не добавляет.
 */
import { useEffect, useState } from 'react';

import { useYear } from '../../../contexts/YearContext';

interface BlockState<T> {
  data: T | null;
  loading: boolean;
}

export const useDistrictBlock = <T,>(
  fetcher: (kato: number, year?: number | null) => Promise<T>,
  /** КАТО из URL страницы; null — территория ещё не определена. */
  kato: number | null,
  /** false — блока на экране нет: не грузим. */
  enabled = true,
): BlockState<T> => {
  const { selectedYear } = useYear();
  const [state, setState] = useState<BlockState<T>>({
    data: null,
    loading: false,
  });

  useEffect(() => {
    if (kato === null || !enabled) {
      setState({ data: null, loading: false });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true });
    fetcher(kato, selectedYear)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false });
      })
      .catch((err) => {
        // Блок без данных рисует «нет данных», а не ошибку интерфейса;
        // 403 по чужой территории страница перехватывает редиректом.
        console.error('useDistrictBlock failed', err);
        if (!cancelled) setState({ data: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, kato, selectedYear, enabled]);

  return state;
};

/**
 * Показывать блок или нет по статусу ответа (US-12 §2).
 *
 * `DISABLED` — супер-админ выключил; `NOT_APPLICABLE` — блок к территории
 * не применим (город областного значения у административного деления).
 * Оба означают «блока нет вовсе», и ни один не подменяется «нет данных».
 *
 * `LEVEL_NOT_SUPPORTED` (операция не обслуживает уровень КАТО) блок
 * оставляет на экране с «нет данных»: состав вкладки одинаков на всех
 * уровнях (решение пользователя), и пропавший блок читался бы как
 * «такого раздела здесь нет», а не как «источник пока не отдаёт уровень».
 *
 * `null` (ответа нет — сеть, 5xx) блок тоже оставляет: заголовок с «нет
 * данных» честнее исчезнувшего виджета.
 */
export const isBlockShown = (
  status: 'OK' | 'NO_DATA' | 'NOT_APPLICABLE' | 'LEVEL_NOT_SUPPORTED' | 'DISABLED' | null,
): boolean => status !== 'DISABLED' && status !== 'NOT_APPLICABLE';
